import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { PUSH_CONFIG } from '@/config/push';

interface VapidConfig {
  configured: boolean;
  valid: boolean;
  publicKey: string | null;
  validationError?: string;
  loading: boolean;
  error: string | null;
}

interface TestResult {
  success: boolean;
  sent?: number;
  total?: number;
  error?: string;
}

export function useVapidConfig() {
  const [config, setConfig] = useState<VapidConfig>({
    configured: false,
    valid: false,
    publicKey: PUSH_CONFIG.VAPID_PUBLIC_KEY, // Fallback to hardcoded
    loading: true,
    error: null,
  });

  const fetchConfig = async () => {
    try {
      setConfig(prev => ({ ...prev, loading: true, error: null }));

      const { data, error } = await supabase.functions.invoke('validate-vapid', {
        body: { action: 'validate' }
      });

      if (error) {
        console.error('Error fetching VAPID config:', error);
        // Use fallback config
        setConfig({
          configured: true,
          valid: true, // Assume valid if using fallback
          publicKey: PUSH_CONFIG.VAPID_PUBLIC_KEY,
          loading: false,
          error: 'Usando configuração local',
        });
        return;
      }

      setConfig({
        configured: data.configured,
        valid: data.valid,
        publicKey: data.publicKey || PUSH_CONFIG.VAPID_PUBLIC_KEY,
        validationError: data.validationError,
        loading: false,
        error: null,
      });

    } catch (err) {
      console.error('Error in useVapidConfig:', err);
      setConfig({
        configured: true,
        valid: true,
        publicKey: PUSH_CONFIG.VAPID_PUBLIC_KEY,
        loading: false,
        error: 'Erro ao validar configuração',
      });
    }
  };

  const sendTestPush = async (userId: string): Promise<TestResult> => {
    try {
      const { data, error } = await supabase.functions.invoke('validate-vapid', {
        body: { action: 'test_push', user_id: userId }
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return data.testResult || { success: false, error: 'Resposta inválida' };

    } catch (err) {
      return { 
        success: false, 
        error: err instanceof Error ? err.message : 'Erro desconhecido' 
      };
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  return {
    ...config,
    refetch: fetchConfig,
    sendTestPush,
  };
}